'use client';

import { type ReactNode, useLayoutEffect, useRef, useState } from 'react';

/** Space kept between the menu and the window's top or bottom edge. */
const WINDOW_GAP = 8;
/** The menu's own bottom padding, the least that sits under the region. */
const MENU_BOTTOM_PADDING = 8;
/** Never shrink below about three rows, even in a very short window. */
const MIN_HEIGHT = 108;

type MenuScrollRegionProps = {
  children: ReactNode;
  /** A fixed cap from the frame, if any (the filter values list's 320). */
  maxHeight?: number;
  className?: string;
};

/** The open trigger whose bottom edge the package placed the menu under, found once at open. */
const findTrigger = (bottom: number) =>
  Array.from(document.querySelectorAll<HTMLElement>('[aria-haspopup][aria-expanded="true"]')).find(
    (element) => Math.abs(element.getBoundingClientRect().bottom - bottom) < 1,
  );

/**
 * Moves a top-level menu above its trigger when the whole menu doesn't fit below it and there's
 * more room above. Returns the region's height cap when it flips, or undefined when it doesn't.
 * Reads the trigger where it is now, so it holds after a scroll the package hasn't re-placed yet.
 * A menu that is already above stays there while it has the more room, so typing a search that
 * shrinks its list doesn't make it jump under the trigger.
 */
const flipAbove = (
  menu: HTMLElement,
  region: HTMLElement,
  trigger: DOMRect,
  isAbove: boolean,
  maxHeight?: number,
) => {
  const style = getComputedStyle(menu);
  const gap = Number.parseFloat(style.marginTop) || 0;
  const chrome = menu.getBoundingClientRect().height - region.getBoundingClientRect().height;
  // A menu with its own cap (the assignee picker's 284) never grows past it.
  const full = Math.min(
    chrome + Math.min(region.scrollHeight, maxHeight ?? Number.POSITIVE_INFINITY),
    Number.parseFloat(style.maxHeight) || Number.POSITIVE_INFINITY,
  );
  const spaceBelow = window.innerHeight - (trigger.bottom + gap) - WINDOW_GAP;
  const spaceAbove = trigger.top - gap - WINDOW_GAP;
  if (spaceAbove <= spaceBelow || (!isAbove && full <= spaceBelow)) return undefined;
  // Anchored by its bottom edge: -100% is the menu's own height, so the menu stays against the
  // trigger when its rows change (a search that shrinks the list) and when the package moves it.
  menu.style.translate = `0 calc(-100% - ${trigger.height + 2 * gap}px)`;
  return Math.min(full, spaceAbove) - chrome;
};

/**
 * DS-GAP(menu-scroll-region): `MenuContent` and `MenuSubContent` have no max height or scrolling
 * body (as the assignee picker's fixed 284 cap in `ServiceRequestListCells.tsx`), and `MenuContent`
 * always opens below its trigger with no collision flip (0.33.3 `usePopoverPosition`). This is the
 * fix for menus whose height the frames draw only for an 800 high window: the rows scroll inside the
 * menu once it would run past the bottom of the window, and a top-level menu that doesn't fit below
 * its trigger opens above it when there's more room there (the CSS `translate` property, which the
 * package doesn't set, so React leaves it alone). Rows the menu draws after the region (Create Work
 * Order, Save a New View) keep their place under it. Not drawn; kept so every item stays reachable
 * on short windows, including from the last row.
 */
export const MenuScrollRegion = ({ children, maxHeight, className }: MenuScrollRegionProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [fitHeight, setFitHeight] = useState<number>();

  useLayoutEffect(() => {
    const region = ref.current;
    const menu = region?.closest<HTMLElement>('[role="menu"], [role="dialog"]');
    if (!region || !menu) return;
    // Submenus are placed by the package beside their parent row, so only top-level menus flip.
    const placed = menu.hasAttribute('data-dropdown-menu-sub') ? undefined : menu;
    let trigger: HTMLElement | undefined;
    const update = () => {
      const gap = Number.parseFloat(getComputedStyle(menu).marginTop) || 0;
      trigger ??= placed && findTrigger(menu.getBoundingClientRect().top - gap);
      const triggerRect = trigger?.getBoundingClientRect();
      const flipped =
        placed && triggerRect
          ? flipAbove(placed, region, triggerRect, placed.style.translate !== '', maxHeight)
          : undefined;
      if (flipped !== undefined) {
        setFitHeight(flipped);
        return;
      }
      if (placed) placed.style.translate = '';
      const menuRect = menu.getBoundingClientRect();
      const rect = region.getBoundingClientRect();
      const below = Math.max(MENU_BOTTOM_PADDING, menuRect.bottom - rect.bottom);
      // Where the package puts the region under the trigger, even before it re-renders on scroll.
      const top = triggerRect ? triggerRect.bottom + gap + rect.top - menuRect.top : rect.top;
      setFitHeight(Math.max(MIN_HEIGHT, window.innerHeight - top - WINDOW_GAP - below));
    };
    update();
    // A top-level menu is placed after its first render, so measure again once it has moved; after
    // that, again whenever its rows change size, any ancestor scrolls or the window resizes.
    let frame = window.requestAnimationFrame(update);
    const schedule = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(update);
    };
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    observer?.observe(menu);
    window.addEventListener('scroll', schedule, true);
    window.addEventListener('resize', schedule);
    return () => {
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('resize', schedule);
      if (placed) placed.style.translate = '';
    };
  }, [maxHeight]);

  const cap = Math.min(
    maxHeight ?? Number.POSITIVE_INFINITY,
    fitHeight ?? Number.POSITIVE_INFINITY,
  );
  return (
    <div
      ref={ref}
      data-menu-scroll-region=""
      className={`flex min-h-0 flex-col overflow-y-auto ${className ?? ''}`}
      style={Number.isFinite(cap) ? { maxHeight: cap } : undefined}
    >
      {children}
    </div>
  );
};
